/**
 * Global Error Handler Service
 * Handles all API error responses and provides consistent error handling across the application
 */
import { AxiosError } from 'axios';
import {
  ApiErrorResponse,
  ApiErrorDetails,
} from '../../types/common/api.types';
import { ErrorType, HttpStatus, TOKEN, USER } from '../../constants';
import { logger } from '../../utils/logger';
import { notifications } from '@mantine/notifications';

export interface ErrorHandlerOptions {
  showToast?: boolean;
  logToConsole?: boolean;
  redirectToLogin?: boolean;
  customHandler?: (error: ProcessedError) => void;
}

export interface ProcessedError {
  source: 'server';
  originalError: AxiosError | Error;
  apiError?: ApiErrorResponse;
  errorType: ErrorType;
  status: string;
  message: string;
  details?: ApiErrorDetails;
  timestamp?: string;
  /** Request ID from server (header or error body) for log correlation */
  requestId?: string;
  isApiError: boolean;
  isNetworkError: boolean;
  isValidationError: boolean;
  isAuthenticationError: boolean;
  isAuthorizationError: boolean;
}

export interface ClientValidationError {
  source: 'client';
  errorType: ErrorType.VALIDATION_ERROR;
  message: string;
  details?: ApiErrorDetails;
  timestamp: string;
}

export type DisplayableError = ProcessedError | ClientValidationError;

class ErrorHandlerService {
  private defaultOptions: ErrorHandlerOptions = {
    showToast: true,
    logToConsole: true,
    redirectToLogin: false,
  };

  /**  Process and handle any error (API or general) */
  public handleError(
    error: AxiosError | Error,
    options: Partial<ErrorHandlerOptions> = {}
  ): ProcessedError {
    const mergedOptions = { ...this.defaultOptions, ...options };
    const processedError = this.processError(error);

    // Log error if enabled
    if (mergedOptions.logToConsole) {
      this.logError(processedError);
    }

    // Show toast notification if enabled
    if (mergedOptions.showToast) {
      this.showErrorToast(processedError);
    }

    // Handle authentication errors
    if (
      mergedOptions.redirectToLogin &&
      this.shouldRedirectToLogin(processedError)
    ) {
      this.redirectToLogin();
    }

    // Call custom handler if provided
    if (mergedOptions.customHandler) {
      mergedOptions.customHandler(processedError);
    }

    return processedError;
  }

  /** Process error and extract relevant information */
  public processError(error: AxiosError | Error): ProcessedError {
    if (this.isAxiosError(error)) {
      return this.processAxiosError(error);
    }

    return this.processGeneralError(error);
  }

  /** Process Axios errors (API calls) */
  private processAxiosError(error: AxiosError): ProcessedError {
    const isNetworkError = !error.response && !error.request;
    const apiError = this.extractApiError(error);

    let errorType = ErrorType.UNKNOWN_ERROR;
    let status = 'UNKNOWN';
    let message = 'An unexpected error occurred';
    let details: ApiErrorDetails | undefined;
    let timestamp: string | undefined;

    if (apiError) {
      // API error body; map errorType, status and message from the API error body.
      errorType = this.mapErrorType(apiError.error, apiError.details);
      // If the API body didn't map to a known type, fall back to HTTP status (e.g. 401 → AUTHENTICATION_ERROR)
      // so we still get a specific errorType instead of leaving UNKNOWN_ERROR.
      if (errorType === ErrorType.UNKNOWN_ERROR && error.response?.status) {
        errorType = this.mapHttpStatusToErrorType(error.response.status);
      }
      status = apiError.status;
      message = apiError.message;
      details = apiError.details;
      timestamp = apiError.timestamp;
    } else if (isNetworkError) {
      // Request never got a real response from the server; no API error body. Or never left the client.
      errorType = ErrorType.NETWORK_ERROR;
      status = 'NETWORK_ERROR';
      message = 'Network error. Please check your connection.';
    } else if (error.response) {
      // No API error body; derive errorType, status and message from the HTTP response.
      errorType = this.mapHttpStatusToErrorType(error.response.status);
      status = this.mapHttpStatusToString(error.response.status);
      message = this.extractErrorMessage(error) || 'Request failed';
    }

    /** Request ID is either from the API error body or the HTTP response header (prefer header). */
    const requestId =
      this.getRequestIdFromResponse(error) ?? apiError?.requestId;

    return {
      source: 'server',
      originalError: error,
      apiError: apiError || undefined,
      errorType,
      status,
      message,
      details,
      timestamp,
      requestId,
      isApiError: !!apiError,
      isNetworkError,
      isValidationError: errorType === ErrorType.VALIDATION_ERROR,
      isAuthenticationError: errorType === ErrorType.AUTHENTICATION_ERROR,
      isAuthorizationError: errorType === ErrorType.AUTHORIZATION_ERROR,
    };
  }

  /** Get request ID from response header (Axios normalizes header names to lowercase) */
  private getRequestIdFromResponse(error: AxiosError): string | undefined {
    const id = error.response?.headers?.['x-request-id'];
    return typeof id === 'string' ? id : undefined;
  }

  /** Process general JavaScript errors */
  private processGeneralError(error: Error): ProcessedError {
    return {
      source: 'server',
      originalError: error,
      errorType: ErrorType.UNKNOWN_ERROR,
      status: 'UNKNOWN',
      message: error.message || 'An unexpected error occurred',
      requestId: undefined,
      isApiError: false,
      isNetworkError: false,
      isValidationError: false,
      isAuthenticationError: false,
      isAuthorizationError: false,
    };
  }

  /** Extract API error response from Axios error */
  private extractApiError(error: AxiosError): ApiErrorResponse | null {
    if (error.response?.data && typeof error.response.data === 'object') {
      const data = error.response.data as Record<string, unknown>;
      if (data.success === false && data.error && data.message) {
        return data as unknown as ApiErrorResponse;
      }
    }
    return null;
  }

  /** Extract error message from various error sources */
  private extractErrorMessage(error: AxiosError): string | null {
    if (error.response?.data) {
      const data = error.response.data as Record<string, unknown>;
      if (typeof data.message === 'string') return data.message;
      if (typeof data.error === 'string') return data.error;
    }
    return null;
  }

  /** Map API error type to internal ErrorType enum. Uses error + details.error_type. */
  private mapErrorType(
    apiErrorType: string,
    details?: ApiErrorDetails
  ): ErrorType {
    const normalized = apiErrorType.toUpperCase();
    switch (normalized) {
      case 'VALIDATION_ERROR':
        return ErrorType.VALIDATION_ERROR;
      case 'AUTHENTICATION_ERROR':
      case 'UNAUTHORIZED':
        return ErrorType.AUTHENTICATION_ERROR;
      case 'AUTHORIZATION_ERROR':
      case 'ACCESS_DENIED':
      case 'FORBIDDEN':
        return ErrorType.AUTHORIZATION_ERROR;
      case 'NOT_FOUND_ERROR':
      case 'NOT_FOUND':
        return ErrorType.NOT_FOUND_ERROR;
      case 'INTERNAL_SERVER_ERROR':
        return ErrorType.INTERNAL_SERVER_ERROR;
      default:
        break;
    }
    const detailType = details?.error_type?.toUpperCase();
    if (detailType) {
      if (
        detailType.includes('ACCESSDENIED') ||
        detailType.includes('FORBIDDEN')
      )
        return ErrorType.AUTHORIZATION_ERROR;
      if (
        detailType.includes('UNAUTHORIZED') ||
        detailType.includes('AUTHENTICATION')
      )
        return ErrorType.AUTHENTICATION_ERROR;
      if (detailType.includes('NOTFOUND')) return ErrorType.NOT_FOUND_ERROR;
      if (detailType.includes('VALIDATION')) return ErrorType.VALIDATION_ERROR;
    }
    return ErrorType.UNKNOWN_ERROR;
  }

  /** Map HTTP status code to ErrorType */
  private mapHttpStatusToErrorType(status: number): ErrorType {
    switch (status) {
      case 400:
        return ErrorType.VALIDATION_ERROR;
      case 401:
        return ErrorType.AUTHENTICATION_ERROR;
      case 403:
        return ErrorType.AUTHORIZATION_ERROR;
      case 404:
        return ErrorType.NOT_FOUND_ERROR;
      case 500:
        return ErrorType.INTERNAL_SERVER_ERROR;
      default:
        return ErrorType.UNKNOWN_ERROR;
    }
  }

  /** Map HTTP status code to string representation */
  private mapHttpStatusToString(status: number): string {
    switch (status) {
      case 400:
        return HttpStatus.BAD_REQUEST;
      case 401:
        return HttpStatus.UNAUTHORIZED;
      case 403:
        return HttpStatus.FORBIDDEN;
      case 404:
        return HttpStatus.NOT_FOUND;
      case 500:
        return HttpStatus.INTERNAL_SERVER_ERROR;
      default:
        return 'UNKNOWN';
    }
  }

  /** Check if error should trigger login redirect */
  private shouldRedirectToLogin(error: ProcessedError): boolean {
    return error.isAuthenticationError || error.isAuthorizationError;
  }

  /** Log error to console (developer-facing; stack trace included for debugging, not shown to users). */
  private logError(error: ProcessedError): void {
    const logData = {
      timestamp: new Date().toISOString(),
      errorType: error.errorType,
      status: error.status,
      message: error.message,
      details: error.details,
      requestId: error.requestId,
      isApiError: error.isApiError,
      isNetworkError: error.isNetworkError,
      stack:
        error.originalError instanceof Error
          ? error.originalError.stack
          : undefined,
    };

    if (error.isAuthenticationError || error.isAuthorizationError) {
      logger.error('Auth error:', logData);
    } else if (error.isApiError) {
      logger.error('API Error:', logData);
    } else if (error.isNetworkError) {
      logger.error('Network Error:', logData);
    } else {
      logger.error('General Error:', logData);
    }
  }

  /** Show error toast notification (ARCHITECTURE §4: Mantine notifications) */
  private showErrorToast(error: ProcessedError): void {
    const message = this.getUserFriendlyMessage(error);
    const detailsStr = this.formatErrorDetails(error.details);
    let body = detailsStr ? `${message} — ${detailsStr}` : message;
    if (error.requestId) {
      body += ` (Request ID: ${error.requestId})`;
    }
    notifications.show({
      title: 'Error',
      message: body,
      color: 'red',
      autoClose: 6000,
    });
  }

  /** Format error details for display */
  public formatErrorDetails(details?: ApiErrorDetails): string {
    if (!details) return '';

    return Object.entries(details)
      .map(([field, message]) => `${field}: ${message}`)
      .join(', ');
  }

  /** Redirect to login page */
  private redirectToLogin(): void {
    // Clear any stored authentication data
    localStorage.removeItem(TOKEN);
    localStorage.removeItem(USER);

    // Redirect to login page
    window.location.href = '/login';
  }

  /** Check if error is an Axios error */
  private isAxiosError(error: unknown): error is AxiosError {
    return Boolean(
      error &&
        typeof error === 'object' &&
        'isAxiosError' in error &&
        (error as Record<string, unknown>).isAxiosError === true
    );
  }

  /** Get user-friendly error message */
  public getUserFriendlyMessage(error: ProcessedError): string {
    if (error.isValidationError && error.details) {
      const detailMessages = Object.values(error.details);
      if (detailMessages.length > 0) {
        return detailMessages[0];
      }
    }

    return error.message;
  }

  /** Check if error is retryable */
  public isRetryable(error: ProcessedError): boolean {
    return (
      error.isNetworkError ||
      error.errorType === ErrorType.INTERNAL_SERVER_ERROR ||
      error.status === HttpStatus.INTERNAL_SERVER_ERROR
    );
  }
}

/** Factory for client-side validation errors displayed by ErrorDisplay. */
export function createClientValidationError(
  message: string,
  details?: ApiErrorDetails
): ClientValidationError {
  return {
    source: 'client',
    errorType: ErrorType.VALIDATION_ERROR,
    message,
    details,
    timestamp: new Date().toISOString(),
  };
}

/** Type guard: true when the value is a ProcessedError (e.g. thrown by unwrapServiceResultAsync). */
export function isProcessedError(e: unknown): e is ProcessedError {
  return (
    !!e &&
    typeof e === 'object' &&
    'source' in e &&
    (e as Record<string, unknown>).source === 'server' &&
    'originalError' in e &&
    'errorType' in e &&
    'message' in e
  );
}

export function isClientValidationError(
  e: unknown
): e is ClientValidationError {
  return (
    !!e &&
    typeof e === 'object' &&
    'source' in e &&
    (e as ClientValidationError).source === 'client' &&
    'errorType' in e &&
    (e as ClientValidationError).errorType === ErrorType.VALIDATION_ERROR &&
    'message' in e
  );
}

// Create and export singleton instance
export const errorHandler = new ErrorHandlerService();
export default errorHandler;
